import React, { useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  GlobalStyles
} from '@mui/material';
import {
  Videocam,
  VideocamOff,
  Mic,
  MicOff,
  Settings,
  Refresh,
  VideoCall,
  BlurOn
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const PreCallContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  backgroundColor: '#1a1a1a',
  padding: theme.spacing(4),
}));

const PreviewVideoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 600,
  aspectRatio: '16/9', // Use aspect ratio instead of fixed height
  backgroundColor: '#1e1e1e',
  borderRadius: '20px',
  overflow: 'hidden',
  position: 'relative',
  marginBottom: theme.spacing(3),
  border: '2px solid rgba(103, 126, 234, 0.3)',
  boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '20px',
  }
}));

const PreCallPreview = ({
  previewTracks,
  isPreviewLoading,
  previewError,
  isVideoEnabled,
  isAudioEnabled,
  previewVideoRef,
  togglePreviewVideo,
  togglePreviewAudio,
  refreshPreview,
  onJoinCall,
  onCancel,
  onOpenSettings,
  isConnecting,
  isProvider,
  isBackgroundBlurEnabled,
  isProcessorLoading,
  processorError,
  onBackgroundBlurToggle,
  permissionsGranted,
  isRequestingPermissions,
  deviceError,
  onRequestPermissions
}) => {
  // Handle video track attachment
  const attachVideoTrack = useCallback(() => {
    const videoElement = previewVideoRef.current;
    
    if (previewTracks.video && videoElement) {
      console.log('Attaching video track to preview element');
      try {
        previewTracks.video.attach(videoElement);
        console.log('Video track attached successfully to preview');
      } catch (err) {
        console.error('Error attaching video track to preview:', err);
      }
    }
  }, [previewTracks.video]); // eslint-disable-line react-hooks/exhaustive-deps

  const detachVideoTrack = useCallback(() => {
    const videoElement = previewVideoRef.current;
    
    if (previewTracks.video && videoElement) {
      try {
        previewTracks.video.detach(videoElement);
        console.log('Video track detached from preview');
      } catch (err) {
        console.error('Error detaching video track from preview:', err);
      }
    }
  }, [previewTracks.video]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    attachVideoTrack();
    
    // Cleanup function to detach track
    return () => {
      detachVideoTrack();
    };
  }, [attachVideoTrack, detachVideoTrack]);

  return (
    <PreCallContainer>
      <GlobalStyles styles={{
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 }
        }
      }} />
      <Card sx={{ 
        width: '100%', 
        maxWidth: 800, 
        p: 3,
        backgroundColor: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <CardContent>
          {/* HIPAA Compliance Message */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1,
            mb: 3,
            p: 2,
            backgroundColor: 'rgba(74, 222, 128, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(74, 222, 128, 0.3)'
          }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: '#4ade80',
              animation: 'pulse 2s infinite'
            }} />
            <Typography variant="body2" sx={{ 
              color: '#4ade80',
              fontWeight: 500
            }}>
              This call is encrypted for HIPAA compliance
            </Typography>
          </Box>
          
          {/* Video Preview */}
          <PreviewVideoContainer>
            {isPreviewLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                backgroundColor: 'rgba(0,0,0,0.8)'
              }}>
                <CircularProgress sx={{ color: '#6b7eef' }} />
              </Box>
            ) : (
              <>
                <video 
                  ref={previewVideoRef} 
                  autoPlay 
                  playsInline 
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
                {!previewTracks.video && !previewError && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    textAlign: 'center',
                    p: 2
                  }}>
                  </Box>
                )}
              </>
            )}
            {previewError && (
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                textAlign: 'center',
                p: 2
              }}>
              </Box>
            )}
            {/* Permission Request Overlay */}
            {!permissionsGranted && !isRequestingPermissions && (
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                color: 'white',
                textAlign: 'center',
                p: 3,
                gap: 2
              }}>
                <Videocam sx={{ fontSize: 60, color: '#6b7eef', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Camera & Microphone Access Required
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 400, mb: 3 }}>
                  To join this secure telehealth session, we need access to your camera and microphone. 
                  This ensures you can communicate with your healthcare provider.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onRequestPermissions}
                  startIcon={<VideoCall />}
                  sx={{ 
                    backgroundColor: '#6b7eef',
                    '&:hover': { backgroundColor: '#5a6fd8' },
                    px: 4,
                    py: 1.5,
                    borderRadius: '25px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  Allow Camera & Microphone Access
                </Button>
              </Box>
            )}
            {/* Permission Request Loading Overlay */}
            {isRequestingPermissions && (
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                color: 'white',
                textAlign: 'center',
                p: 3,
                gap: 2
              }}>
                <CircularProgress size={60} sx={{ color: '#6b7eef', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Requesting Permissions...
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 400 }}>
                  Please allow access to your camera and microphone when prompted by your browser.
                </Typography>
              </Box>
            )}
          </PreviewVideoContainer>
          
          {/* Device Error Display */}
          {deviceError && (
            <Box sx={{ 
              mb: 2,
              p: 2, 
              backgroundColor: 'rgba(239, 68, 68, 0.1)', 
              color: '#ef4444',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {deviceError}
              </Typography>
              {!permissionsGranted && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onRequestPermissions}
                  sx={{ 
                    mt: 1,
                    color: '#ef4444',
                    borderColor: '#ef4444',
                    '&:hover': { 
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      borderColor: '#ef4444'
                    }
                  }}
                >
                  Try Again
                </Button>
              )}
            </Box>
          )}
          
          {/* Preview Controls */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2, 
            mb: 3,
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <IconButton 
              onClick={togglePreviewVideo} 
              sx={{ 
                backgroundColor: isVideoEnabled ? 'rgba(103, 126, 234, 0.2)' : 'rgba(239, 68, 68, 0.2)', 
                color: isVideoEnabled ? '#6b7eef' : '#ef4444',
                border: `1px solid ${isVideoEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                width: 50,
                height: 50,
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  backgroundColor: isVideoEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }
              }}
            >
              {isVideoEnabled ? <Videocam /> : <VideocamOff />}
            </IconButton>
            <IconButton 
              onClick={togglePreviewAudio} 
              sx={{ 
                backgroundColor: isAudioEnabled ? 'rgba(103, 126, 234, 0.2)' : 'rgba(239, 68, 68, 0.2)', 
                color: isAudioEnabled ? '#6b7eef' : '#ef4444',
                border: `1px solid ${isAudioEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                width: 50,
                height: 50,
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  backgroundColor: isAudioEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }
              }}
            >
              {isAudioEnabled ? <Mic /> : <MicOff />}
            </IconButton>
            <IconButton 
              onClick={onBackgroundBlurToggle} 
              disabled={isProcessorLoading || !isVideoEnabled}
              sx={{ 
                backgroundColor: isBackgroundBlurEnabled ? 'rgba(103, 126, 234, 0.2)' : 'rgba(255,255,255,0.1)',
                color: isBackgroundBlurEnabled ? '#6b7eef' : '#ffffff',
                border: `1px solid ${isBackgroundBlurEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(255,255,255,0.2)'}`,
                width: 50,
                height: 50,
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  backgroundColor: isBackgroundBlurEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(255,255,255,0.2)',
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                },
                '&:disabled': {
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  color: 'rgba(255,255,255,0.3)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              {isProcessorLoading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <BlurOn />}
            </IconButton>
            <IconButton 
              onClick={refreshPreview} 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.2)',
                width: 50,
                height: 50,
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }
              }}
            >
              <Refresh />
            </IconButton>
            <IconButton 
              onClick={onOpenSettings} 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.2)',
                width: 50,
                height: 50,
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }
              }}
            >
              <Settings />
            </IconButton>
          </Box>
          
          {/* Processor Error Display */}
          {processorError && (
            <Box sx={{ 
              mb: 2,
              p: 2, 
              backgroundColor: 'rgba(239, 68, 68, 0.1)', 
              color: '#ef4444',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Background Blur Error: {processorError}
              </Typography>
            </Box>
          )}
          
          {/* Join Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={onJoinCall}
              disabled={isConnecting || !!previewError || !permissionsGranted}
              startIcon={isConnecting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <VideoCall />}
              sx={{ 
                px: 4, 
                py: 1.5,
                backgroundColor: 'rgba(103, 126, 234, 0.9)',
                color: 'white',
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: '1rem',
                border: '1px solid rgba(103, 126, 234, 1)',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  backgroundColor: 'rgba(103, 126, 234, 1)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(103, 126, 234, 0.3)'
                },
                '&:disabled': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.5)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              {isConnecting ? 'Joining...' : 'Join Call'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={onCancel}
              sx={{ 
                px: 4, 
                py: 1.5,
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: 'white',
                borderRadius: '12px',
                fontWeight: 500,
                fontSize: '1rem',
                border: '1px solid rgba(255,255,255,0.3)',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      {/* Error Display */}
      {previewError && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          backgroundColor: 'rgba(239, 68, 68, 0.1)', 
          color: '#ef4444',
          borderRadius: '12px',
          width: '100%',
          maxWidth: 800,
          border: '1px solid rgba(239, 68, 68, 0.3)',
          backdropFilter: 'blur(10px)'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            ⚠️ Error: {previewError}
          </Typography>
        </Box>
      )}
    </PreCallContainer>
  );
};

export default PreCallPreview; 